'use client';

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { buildApiUrl, API_ENDPOINTS } from '@/constants/api';
import FilterTable from './FilterTable';
import Button1 from './Button1';
import AddToCart from './AddToCart';
import LoadingOverlay from '@/components/LoadingOverlay';
import { COLORS } from '@/constants/colors';
import { TYPOGRAPHY, FONT_SIZE } from '@/constants/typography';
import { SPACING } from '@/constants/spacing';
import { BREAKPOINTS } from '@/constants/breakpoints';

const tableHeader = [
  {
    id: 0,
    name: 'title',
    label: 'Green Filter #',
    maxWidth: 91,
  },
  {
    id: 2,
    name: 'description',
    label: 'Description',
    maxWidth: 82,
  },
];

const CCElement1 = () => {
  const [tableData, setTableData] = useState([]);
  const [productData, setProductData] = useState<any>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getTableData();
  }, []);

  const getTableData = async () => {
    setLoading(true);

    const product_type_id = 8;
    try {
      const url = buildApiUrl(API_ENDPOINTS.product, {
        product_type_id,
        pageNo: 1,
        pageSize: 200,
        is_active: 1,
      });

      const res = await fetch(url);

      if (!res.ok) {
        throw new Error(`API request failed with status ${res.status}`);
      }

      const data = await res.json();

      if (data?.ResponseCode === 'Success') {
        const tableDataT = data?.data?.rows || [];
        setTableData(tableDataT);
        const productDataT = tableDataT.find((p: any) => p.id === 1890) || tableDataT?.[0] || {};
        setProductData(productDataT);
      } else {
        console.error('API returned unsuccessful response:', data);
      }
    } catch (error) {
      console.error('Failed to fetch cleaner care products:', error);
      // Set empty data to prevent undefined errors
      setTableData([]);
      setProductData({});
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <LoadingOverlay loading={loading} />

      <PageWrapper>
        {/* Intro Section */}
        <IntroSection>
          <SectionTitle>Air Filter Cleaner & Maintenance Kit</SectionTitle>
          <IntroText>
            Keep your Green High Performance Air Filter in peak condition with our specially formulated cleaner
            and recharge oil. Designed for optimal performance and engine protection.
          </IntroText>
        </IntroSection>

        {/* Product Showcase */}
        <ProductShowcase>
          <ProductImageWrapper>
            <ProductImage src="/assets/cleaner-p-800.webp" alt="Green Filter Cleaner Kit" />
          </ProductImageWrapper>
          <ProductInfo>
            <ProductName>Green Filter Cleaner & Oil Kit</ProductName>
            <ProductSKU>{productData?.title}</ProductSKU>

            <ShopifyCartWrapper>
              <AddToCart
                buyButtonId={productData?.buy_url}
                id={productData?.id}
                background={COLORS.brandGreen}
                color="#fff"
                hidePrice={false}
                isLeft={true}
              />
            </ShopifyCartWrapper>

            <ProductFeatures>
              <Feature>✓ 12 oz. Cleaner Spray - Ready to use</Feature>
              <Feature>✓ 8 oz. Recharge Oil - Premium formula</Feature>
              <Feature>✓ Safe for all sensors</Feature>
              <Feature>✓ Professional grade quality</Feature>
            </ProductFeatures>
          </ProductInfo>
        </ProductShowcase>

        {/* PDF Download Section */}
        <PDFSection>
          <PDFCard>
            <PDFContent>
              <PDFTitle>Cleaning Instructions</PDFTitle>
              <PDFDescription>Download our complete step-by-step guide for cleaning and maintaining your Green Filter</PDFDescription>
            </PDFContent>
            <PDFButtonWrapper>
              <Button1
                text="Download PDF"
                isLink
                to="/documents/green-filter-cleaning.pdf"
                target="_blank"
              />
            </PDFButtonWrapper>
          </PDFCard>
        </PDFSection>

        {/* Available Products Table */}
        <TableSection>
          <SectionTitle>Available Products</SectionTitle>
          <FilterTable headers={tableHeader} tableData={tableData} />
        </TableSection>

        {/* Cleaning Instructions */}
        <InstructionsSection>
          <SectionTitle>How to Clean Your Green Filter</SectionTitle>
          <InstructionsGrid>
            <InstructionCard>
              <StepNumber>1</StepNumber>
              <StepTitle>Pre-Cleaning</StepTitle>
              <StepDescription>Tap the filter to remove excess dirt and debris.</StepDescription>
            </InstructionCard>

            <InstructionCard>
              <StepNumber>2</StepNumber>
              <StepTitle>Apply Cleaner</StepTitle>
              <StepDescription>
                Spray with Green Filter Cleaner and let soak for 15 minutes. Never use strong detergents or
                high-pressure water.
              </StepDescription>
            </InstructionCard>

            <InstructionCard>
              <StepNumber>3</StepNumber>
              <StepTitle>Rinse</StepTitle>
              <StepDescription>
                Rinse with warm water from clean side to dirty side. Use gentle water pressure.
              </StepDescription>
            </InstructionCard>

            <InstructionCard>
              <StepNumber>4</StepNumber>
              <StepTitle>Dry</StepTitle>
              <StepDescription>
                Let air dry naturally. Avoid heat as it may shrink the cotton fibers.
              </StepDescription>
            </InstructionCard>

            <InstructionCard>
              <StepNumber>5</StepNumber>
              <StepTitle>Re-Oil</StepTitle>
              <StepDescription>
                Apply Green Filter Oil - 1 spray per 2 square inches. Do not over-oil the filter.
              </StepDescription>
            </InstructionCard>
          </InstructionsGrid>

          <MaintenanceTip>
            <strong>Maintenance Schedule:</strong> Clean every 30,000 miles or sooner in dusty conditions. Visual
            inspection recommended at every oil change.
          </MaintenanceTip>
        </InstructionsSection>

        {/* Why Green Filter Section */}
        <WhySection>
          <SectionTitle>Why Choose Green Filter?</SectionTitle>
          <BenefitsGrid>
            <BenefitCard>
              <BenefitTitle>5 Micron Filtration</BenefitTitle>
              <BenefitText>Traps particles as small as 5 microns for maximum engine protection</BenefitText>
            </BenefitCard>

            <BenefitCard>
              <BenefitTitle>Increased Airflow</BenefitTitle>
              <BenefitText>More potent air/fuel mixture means increased horsepower</BenefitText>
            </BenefitCard>

            <BenefitCard>
              <BenefitTitle>Better Fuel Economy</BenefitTitle>
              <BenefitText>Improved efficiency saves money at the pump</BenefitText>
            </BenefitCard>

            <BenefitCard>
              <BenefitTitle>Reusable & Eco-Friendly</BenefitTitle>
              <BenefitText>Washable design lasts for years, reducing waste</BenefitText>
            </BenefitCard>

            <BenefitCard>
              <BenefitTitle>Sensor Safe</BenefitTitle>
              <BenefitText>Won't interfere with engine monitoring systems</BenefitText>
            </BenefitCard>

            <BenefitCard>
              <BenefitTitle>Made in USA</BenefitTitle>
              <BenefitText>Premium quality, American-made craftsmanship</BenefitText>
            </BenefitCard>
          </BenefitsGrid>
        </WhySection>
      </PageWrapper>
    </>
  );
};

export default React.memo(CCElement1);

// Styled Components
const PageWrapper = styled.div`
  background: ${COLORS.white};
  padding: ${SPACING.section} 0 ${SPACING.sectionLarge};
`;

const IntroSection = styled.section`
  max-width: 800px;
  margin: 0 auto ${SPACING.sectionSmall};
  padding: 0 ${SPACING.lg};
  text-align: center;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    padding: 0 ${SPACING.md};
    margin-bottom: ${SPACING.xl};
  }
`;

const SectionTitle = styled.h2`
  ${TYPOGRAPHY.h2}
  color: ${COLORS.textPrimary};
  margin-bottom: ${SPACING.lg};
  text-align: center;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    font-size: ${FONT_SIZE.heading.md};
  }
`;

const IntroText = styled.p`
  ${TYPOGRAPHY.body}
  color: ${COLORS.textSecondary};
  margin: 0;
`;

const ProductShowcase = styled.section`
  max-width: 1200px;
  margin: 0 auto ${SPACING.sectionLarge};
  padding: 0 ${SPACING.lg};
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${SPACING.sectionSmall};
  align-items: start;

  @media (max-width: ${BREAKPOINTS.tablet}) {
    grid-template-columns: 1fr;
    gap: ${SPACING.xl};
    padding: 0 ${SPACING.md};
  }
`;

const ProductImageWrapper = styled.div`
  background: ${COLORS.gray100};
  border-radius: 12px;
  padding: ${SPACING.xl};
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    padding: ${SPACING.lg};
  }
`;

const ProductImage = styled.img`
  width: 100%;
  max-width: 500px;
  height: auto;
`;

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.lg};
`;

const ProductName = styled.h3`
  ${TYPOGRAPHY.h3}
  color: ${COLORS.textPrimary};
  margin: 0;
`;

const ProductSKU = styled.p`
  ${TYPOGRAPHY.body}
  color: ${COLORS.textMuted};
  margin: 0 0 ${SPACING.lg};
`;

const ShopifyCartWrapper = styled.div`
  margin-bottom: ${SPACING.xl};

  /* Shopify styles are handled by AddToCart component */
`;

const ProductFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm};
`;

const Feature = styled.li`
  ${TYPOGRAPHY.body}
  color: ${COLORS.textPrimary};
  display: flex;
  align-items: center;
`;

const CTAButton = styled.div`
  margin-top: ${SPACING.md};
`;

const PDFSection = styled.section`
  max-width: 1200px;
  margin: 0 auto ${SPACING.sectionLarge};
  padding: 0 ${SPACING.lg};

  @media (max-width: ${BREAKPOINTS.mobile}) {
    padding: 0 ${SPACING.md};
  }
`;

const PDFCard = styled.div`
  background: linear-gradient(135deg, ${COLORS.brandGreen} 0%, ${COLORS.brandGreenDark} 100%);
  border-radius: 12px;
  padding: ${SPACING.xl} ${SPACING.sectionSmall};
  display: flex;
  align-items: center;
  gap: ${SPACING.xl};
  box-shadow: 0 4px 16px rgba(0, 173, 35, 0.2);

  @media (max-width: ${BREAKPOINTS.tablet}) {
    flex-direction: column;
    text-align: center;
    padding: ${SPACING.xl} ${SPACING.lg};
  }
`;

const PDFContent = styled.div`
  flex: 1;
`;

const PDFTitle = styled.h3`
  ${TYPOGRAPHY.h3}
  color: ${COLORS.white};
  margin: 0 0 ${SPACING.sm};
`;

const PDFDescription = styled.p`
  ${TYPOGRAPHY.body}
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
`;

const PDFButtonWrapper = styled.div`
  flex-shrink: 0;

  button {
    background: ${COLORS.white} !important;
    color: ${COLORS.brandGreen} !important;

    &:hover {
      background: ${COLORS.gray100} !important;
      transform: translateY(-2px);
    }
  }
`;

const TableSection = styled.section`
  max-width: 1200px;
  margin: 0 auto ${SPACING.sectionLarge};
  padding: 0 ${SPACING.lg};

  @media (max-width: ${BREAKPOINTS.mobile}) {
    padding: 0 ${SPACING.md};
  }
`;

const InstructionsSection = styled.section`
  max-width: 1200px;
  margin: 0 auto ${SPACING.sectionLarge};
  padding: ${SPACING.section} ${SPACING.lg};
  background: ${COLORS.bgGray};
  border-radius: 12px;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    padding: ${SPACING.xl} ${SPACING.md};
    margin-left: ${SPACING.md};
    margin-right: ${SPACING.md};
  }
`;

const InstructionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${SPACING.lg};
  margin-bottom: ${SPACING.xl};
`;

const InstructionCard = styled.div`
  background: ${COLORS.white};
  padding: ${SPACING.lg};
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const StepNumber = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${COLORS.brandGreen};
  color: ${COLORS.white};
  display: flex;
  align-items: center;
  justify-content: center;
  ${TYPOGRAPHY.h3}
  font-weight: 700;
  margin-bottom: ${SPACING.md};
`;

const StepTitle = styled.h4`
  ${TYPOGRAPHY.h4}
  color: ${COLORS.textPrimary};
  margin: 0 0 ${SPACING.sm};
`;

const StepDescription = styled.p`
  ${TYPOGRAPHY.body}
  color: ${COLORS.textSecondary};
  margin: 0;
`;

const MaintenanceTip = styled.div`
  ${TYPOGRAPHY.body}
  color: ${COLORS.textPrimary};
  background: ${COLORS.white};
  padding: ${SPACING.lg};
  border-left: 4px solid ${COLORS.brandGreen};
  border-radius: 4px;
  text-align: center;

  strong {
    color: ${COLORS.brandGreen};
  }
`;

const WhySection = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${SPACING.lg};

  @media (max-width: ${BREAKPOINTS.mobile}) {
    padding: 0 ${SPACING.md};
  }
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${SPACING.xl};

  @media (max-width: ${BREAKPOINTS.mobile}) {
    grid-template-columns: 1fr;
    gap: ${SPACING.lg};
  }
`;

const BenefitCard = styled.div`
  text-align: center;
  padding: ${SPACING.xl};
  background: ${COLORS.white};
  border: 1px solid ${COLORS.border};
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }
`;

const BenefitTitle = styled.h4`
  ${TYPOGRAPHY.h4}
  color: ${COLORS.textPrimary};
  margin: 0 0 ${SPACING.sm};
`;

const BenefitText = styled.p`
  ${TYPOGRAPHY.body}
  color: ${COLORS.textSecondary};
  margin: 0;
`;
